import { useState, useEffect } from "react";
import BaseModal from "../../BaseComponents/BaseModal/BaseModal";
import ToggleSwitch from "../../BaseComponents/Toggle/ToggleSwitch";
import styles from "./AddDepositModal.module.css";

const empty = {
  name: "",
  depositType: "Percentage",
  depositValue: "",
  productDependent: false,
  startDate: "",
  endDate: "",
  status: true,
};

const toForm = (data) => {
  if (!data) return empty;
  return {
    name:             data.name             || "",
    depositType:      data.depositType      || "Percentage",
    depositValue:     data.depositValue     ?? "",
    productDependent: data.productDependent ?? false,
    startDate:        data.startDate        || "",
    endDate:          data.endDate          || "",
    status:           data.status           ?? true,
  };
};

const AddDepositModal = ({ open, onClose, initialData, onSave, saveLoading = false }) => {
  const [form, setForm] = useState(() => toForm(initialData));
  const isEdit = !!initialData;

  useEffect(() => {
    if (open) setForm(toForm(initialData));
  }, [open, initialData]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = () => {
    if (!form.name.trim() || form.depositValue === "") return;
    onSave({ ...form, depositValue: parseFloat(form.depositValue) });
  };

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={`${isEdit ? "Edit" : "Add"} Deposit Rule`}
      width="540px"
      footer={
        <>
          <button className={styles.cancelBtn} onClick={onClose} disabled={saveLoading}>
            Cancel
          </button>
          <button
            className={`${styles.primaryBtn} ${saveLoading ? styles.primaryBtnLoading : ""}`}
            onClick={handleSubmit}
            disabled={saveLoading}
          >
            {saveLoading
              ? <><span className={styles.spinner} /> Saving…</>
              : isEdit ? "Save Changes" : "+ Add Rule"
            }
          </button>
        </>
      }
    >
      <div className={styles.formGrid}>

        {/* Rule Name */}
        <div className={`${styles.field} ${styles.fullWidth}`}>
          <label>Rule Name</label>
          <input
            placeholder="e.g. Standard Flight Deposit"
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
          />
        </div>

        {/* Deposit Type */}
        <div className={styles.field}>
          <label>Deposit Type</label>
          <select value={form.depositType} onChange={(e) => set("depositType", e.target.value)}>
            <option value="Percentage">Percentage (%)</option>
            <option value="Fixed">Fixed Amount (€)</option>
          </select>
        </div>

        {/* Deposit Value */}
        <div className={styles.field}>
          <label>Deposit Value</label>
          <div className={styles.inputWithAddon}>
            <span className={styles.addon}>
              {form.depositType === "Percentage" ? "%" : "€"}
            </span>
            <input
              type="number"
              min="0"
              step="0.01"
              placeholder={form.depositType === "Percentage" ? "e.g. 20" : "e.g. 150.00"}
              value={form.depositValue}
              onChange={(e) => set("depositValue", e.target.value)}
              className={styles.addonInput}
            />
          </div>
        </div>

        {/* Start Date */}
        <div className={styles.field}>
          <label>Start Date</label>
          <input
            type="date"
            value={form.startDate}
            onChange={(e) => set("startDate", e.target.value)}
          />
        </div>

        {/* End Date */}
        <div className={styles.field}>
          <label>End Date</label>
          <input
            type="date"
            value={form.endDate}
            onChange={(e) => set("endDate", e.target.value)}
          />
        </div>

        {/* Toggles */}
        <div className={styles.toggleRow}>
          <div className={styles.field}>
            <label>Product Dependent</label>
            <ToggleSwitch
              checked={form.productDependent}
              onChange={() => set("productDependent", !form.productDependent)}
            />
          </div>
          <div className={styles.field}>
            <label>Status</label>
            <ToggleSwitch
              checked={form.status}
              onChange={() => set("status", !form.status)}
            />
          </div>
        </div>

      </div>

      <div className={styles.infoBox}>
        ⚠️ Deposit rules outside their validity period are never applied, regardless of their status.
      </div>
    </BaseModal>
  );
};

export default AddDepositModal;